import {initializePaddle, type CheckoutSettings, type Paddle} from '@paddle/paddle-js';
import {PADDLE_CONFIG} from '/src/const';
import {activeLocale} from '/src/i18n/i18n';
import type {Locale} from '/src/i18n/locales';
import {logEvent} from '/src/logger';
import {capturePostHogEvent, logPostHogInfo} from '/src/posthog';

let paddleInitializationPromise: Promise<Paddle | undefined> | null = null;

export type TPaddlePlan = keyof typeof PADDLE_CONFIG.prices;
export type TPaddleBillingCycle = 'week' | 'month' | 'annual' | 'lifetime';

function createPaddleInitialization(): Promise<Paddle | undefined> {
    // Initialize Paddle once with a shared analytics callback.
    return initializePaddle({
        token: PADDLE_CONFIG.clientToken,
        eventCallback(data) {
            // Forward named Paddle events into the existing analytics stream.
            if (data?.name) {
                logEvent(`paddle_${data.name}`);
            }
        },
    });
}

export function initializePaddleSingleton(): Promise<Paddle | undefined> {
    // Reuse the same Paddle initialization promise across the whole app lifetime.
    paddleInitializationPromise ??= createPaddleInitialization().catch((error) => {
        // Drop the cached promise after a failed init so the next attempt can retry.
        paddleInitializationPromise = null;
        throw error;
    });
    return paddleInitializationPromise;
}

export async function setPaddleCustomer(customerId: string): Promise<void> {
    if (!paddleInitializationPromise) return;
    const paddle = await paddleInitializationPromise;
    paddle?.Update({pwCustomer: {id: customerId}});
}

/** Checkout overlay settings; Paddle shows the checkout in the user's Lopaka language. */
export function paddleCheckoutSettings(locale: Locale): CheckoutSettings {
    return {
        displayMode: 'overlay',
        variant: 'one-page',
        successUrl: 'https://lopaka.app/success',
        locale,
    };
}

export async function openPaddleCheckout(
    email: string,
    userId: string,
    plan: TPaddlePlan,
    billingCycle: TPaddleBillingCycle,
    discountCode?: string,
) {
    // Resolve the shared Paddle instance before opening checkout.
    const paddle = await initializePaddleSingleton();
    if (!paddle) {
        return;
    }
    const planPrices = PADDLE_CONFIG.prices[plan] as Partial<Record<TPaddleBillingCycle, string>>;
    const priceId = planPrices[billingCycle];
    if (!priceId) {
        console.error(`Missing Paddle price for ${plan}:${billingCycle}`);
        return;
    }
    // Open checkout with the selected price and the current user context.
    paddle.Checkout.open({
        settings: paddleCheckoutSettings(activeLocale()),
        items: [
            {
                priceId,
                quantity: 1,
            },
        ],
        customer: {
            email,
        },
        customData: {
            user_id: userId,
            email,
        },
        ...(discountCode ? {discountCode} : {}),
    });
    capturePostHogEvent('checkout_opened', {
        plan,
        billing_cycle: billingCycle,
        has_discount: Boolean(discountCode),
    });
    logPostHogInfo('checkout launch completed', {
        plan,
        billing_cycle: billingCycle,
        has_discount: Boolean(discountCode),
    });
}

export async function openRetainCancellationFlow(subscriptionId: string): Promise<boolean> {
    try {
        const paddle = await initializePaddleSingleton();
        if (!paddle) return false;

        await paddle.Retain.initCancellationFlow({subscriptionId});
        return true;
    } catch {
        return false;
    }
}

export function resetPaddleSingleton(): void {
    // Reset singleton state to keep unit tests isolated from one another.
    paddleInitializationPromise = null;
}
